import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
//third party
import {
  Subject,
  timer,
  Observable,
  Subscription,
  catchError,
  throwError,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CompanyInfo, Master, loginAccount } from '../shared/models/company';
import { SharedService } from '../shared/services/shared.service';

//models
@Injectable({
  providedIn: 'root',
})
export class StartupService {
  timeToLogout: number = 5000; //100000 //00
  logoutCountdown!: Subscription;
  storedTimeToLogout!: number;
  company_info: CompanyInfo = new CompanyInfo();

  constructor(
    private router: Router,
    private http: HttpClient,
    private sharedService: SharedService,
    private modalService: BsModalService,
  ) {}
  // subjects
  user_successfully_logged_in$: Subject<loginAccount> = new Subject();

  startLogOutTimer() {
    this.storedTimeToLogout = this.timeToLogout;
    this.logoutCountdown = this.createCountdown(this.timeToLogout).subscribe(
      (timeLeft) => {
        // this.matDialog.open(LogoutConfirmComponent)
        this.ourLogoutFunction();
      }
    );
  }
  createCountdown(timeToLogout: number): Observable<number> {
    return timer(timeToLogout, 1000);
  }
  cancelLogout() {
    this.logoutCountdown.unsubscribe();
  }
  ourLogoutFunction() {
    // call our specific log out logic
    while (this.modalService.getModalsCount() > 0) { this.modalService.hide(); }
    alert('USER LOGGED OUT!');
    this.cancelLogout();
    this.startAppAtLogin('login');
  }
  startAppAtLogin(page: string) {
    setTimeout(() => {
      if (this.router.url === '/') {
        this.router.navigate([page]);
      } else if (this.router.url !== '/login') {
        this.router.navigate([page]);
      }
    }, 500);
  }

  private getLoginErrorMessage(error: HttpErrorResponse): string {
    const serverMessage =
      typeof error?.error === 'string' ? error.error : error?.error?.message;
    const combined = `${serverMessage || ''} ${error?.message || ''}`.toLowerCase();

    if (error.status === 429) {
      return 'Too many sign-in attempts. Please wait a few minutes and try again.';
    }

    if (
      error.status === 0 ||
      error.status >= 500 ||
      combined.includes('buffering timed out') ||
      combined.includes('timed out') ||
      combined.includes('econnrefused') ||
      combined.includes('network error') ||
      combined.includes('failed to fetch')
    ) {
      return 'The server is momentarily unavailable. Please try again in a minute.';
    }

    if (combined.includes('password is incorrect') || combined.includes('not a registered email')) {
      return 'Email or password is incorrect.';
    }

    return serverMessage || 'Unable to sign in right now. Please try again.';
  }

  private shouldReportLoginError(error: HttpErrorResponse): boolean {
    const serverMessage =
      typeof error?.error === 'string' ? error.error : error?.error?.message;
    const combined = `${serverMessage || ''} ${error?.message || ''}`.toLowerCase();

    return (
      error.status === 0 ||
      error.status >= 500 ||
      combined.includes('buffering timed out') ||
      combined.includes('econnrefused') ||
      combined.includes('failed to fetch')
    );
  }

  getVerifyCredentials(email: string, password: string, rememberMe: boolean = false): Observable<loginAccount> {
    const credentials = { email, password, rememberMe };
    return this.http
      .post<any>(`http://localhost:3000/api/login`, credentials)
      .pipe(
        catchError((error) => {
          // Don't show error toast here — let login.component handle it,
          // so it can attempt offline login before raising user-facing errors.

          if (this.shouldReportLoginError(error)) {
            Sentry.withScope((scope) => {
              scope.setTag('flow', 'login');
              scope.setLevel('error');
              scope.setExtra('httpStatus', error.status);
              scope.setExtra(
                'serverMessage',
                typeof error?.error === 'string' ? error.error : error?.error?.message,
              );
              Sentry.captureException(error);
            });
          }

          return throwError(() => error);
        }),
        map((response) => {
          let successful_login = new loginAccount();
          successful_login.email = response['user'].email;
          successful_login.company_id = response['user'].company_id;
          successful_login.id = response['user']._id;
          successful_login.username = response['user'].username;
          successful_login.role = response['user'].role;
          successful_login.name_first = response['user'].name_first;
          successful_login.name_last = response['user'].name_last;
          successful_login.user_id = response['user'].user_id;
          (successful_login as any).rememberMeToken = response['rememberMeToken'];
          
          this.user_successfully_logged_in$.next(successful_login);
          return successful_login;
        })
      );
  }

  verifyToken(): Observable<loginAccount> {
    return this.http
      .post<any>(`http://localhost:3000/api/auth/verify-token`, {})
      .pipe(
        map((response) => {
          let account = new loginAccount();
          account.email = response['user'].email;
          account.company_id = response['user'].company_id;
          account.id = response['user']._id;
          account.username = response['user'].username;
          account.role = response['user'].role;
          account.name_first = response['user'].name_first;
          account.name_last = response['user'].name_last;
          account.user_id = response['user'].user_id;
          account.must_change_password = response['user'].must_change_password;
          return account;
        })
      );
  }

  logout(): Observable<any> {
    return this.http.post(`http://localhost:3000/api/auth/logout`, {});
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`http://localhost:3000/api/auth/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`http://localhost:3000/api/auth/reset-password`, { token, newPassword });
  }
}
